import React from "react";
import { DateBuilder, ActivityListing, ActivityType, ActionPlanRiskConsequence, ActionPlanRiskLikelihood, ScoredSelectionStats } from "../../../amorg-common";
import { ButtonRow, BorderRow } from "../../../designSystem/button/ButtonRow";
import { colors } from "../../../designSystem/colors/color";
import { H2, H3 } from "../../../designSystem/typography/Heading";
import { TimelineGraphEvent, TimelineGraphYearRow } from "../../../designSystem/graphs/timelineGraph/TimelineGraphYearRow";
import { SegmentBar } from "../../../designSystem/graphs/SegmentBar";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { SegmentRatioBar } from "../../../designSystem/graphs/SegmentRatioBar";
import { VerticalBarGraph } from "../../../designSystem/graphs/VerticalBarGraph";
import { RadialChart } from "../../../designSystem/graphs/RadialChart";
import { RadialAvgChart } from "../../../designSystem/graphs/RadialAvgChart";
import { RadialPctChart } from '../../../designSystem/graphs/RadialPctChart';
import { CompletionPanel } from "../../../designSystem/graphs/CompletionPanel";
import { RadialPieChart } from "../../../designSystem/graphs/RadialPieChart";
import { Badge } from "../../../designSystem/Badge";
import { DetailedRiskAssessmentEvaluator } from "../../../designSystem/graphs/DetailedRiskAssessmentEvaluator";
import { DialGauge } from "../../../designSystem/graphs/DialGauge";
import { DesignSystemPageSection, DesignSystemVariantSection } from "./DesignSystemPageSection";
import { SegmentStatsComparisonGraph } from "../../../designSystem/graphs/SegmentStatsComparisonGraph";
import { RiskAssessmentEvaluator } from "../../../designSystem/graphs/RiskAssessmentEvaluator";
import { ActivityGraph } from "../../dashboard/_components/ActivityGraph";
import { AnswerGraphics } from "../../workplaceassessments/[activityId]/_components/tabs/answer/_components/AnswerGraph";
import { AggregatedSurveyAnswerScoreRadialChart } from "../../../designSystem/graphs/SurveyAnswerScoreRadialChart";
import { getMonthStart, toDateString } from "../../../utils/date";
import { ProgressBar } from "../../../designSystem/graphs/ProgressBar";


const graphStartWithOffset = DateBuilder.now();
const graphStart = new DateBuilder(getMonthStart(graphStartWithOffset.date));

export const GraphicsOverview = (): JSX.Element =>
{
    function getActivityListing (data: { id: string, start, end, activityType: ActivityType, title: string, description: string; })
    {
        const a: ActivityListing = {
            ...data,
            invitationType: 'InviteOnly',
            location: 'Oslo',
            workgroupId: '1',
            workgroupName: 'Workgroup 1',
            owner: null,
            participantCount: 0,
        };
        return a;
    }

    const activities: ActivityListing[] = [
        getActivityListing({ id: '1', start: graphStart.addDays(3).date, end: graphStart.addMonths(1).date, activityType: 'WorkgroupMeeting', title: 'An A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '2', start: graphStart.addDays(3).date, end: graphStart.addMonths(1).date, activityType: 'WorkgroupMeeting', title: 'An A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '3', start: graphStart.addDays(3).date, end: graphStart.addMonths(1).addDays(10).date, activityType: 'WorkgroupMeeting', title: 'An A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '4', start: graphStart.addDays(23).date, end: graphStart.addMonths(3).date, activityType: 'WorkgroupMeeting', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '5', start: graphStart.addMonths(2).addDays(2).date, end: graphStart.addMonths(3).addDays(2).date, activityType: 'AnnualWorkgroupStatus', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '6', start: graphStart.addMonths(2).addDays(12).date, end: graphStart.addMonths(3).addDays(12).date, activityType: 'AnnualWorkgroupStatus', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '7', start: graphStart.addMonths(4).addDays(2).date, end: graphStart.addMonths(4).addDays(2).date, activityType: 'AnnualWorkgroupStatus', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '8', start: graphStart.addMonths(1).addDays(8).date, end: graphStart.addMonths(1).addDays(45).date, activityType: 'WorkplaceAssessment', title: 'Another A activity', description: 'Lorem ipsum...' }),
    ];

    const [consequence, setConsequence] = React.useState<ActionPlanRiskConsequence>('1 - small');
    const [likelihood, setLikelihood] = React.useState<ActionPlanRiskLikelihood>('1 - unlikely');

    const score5Counts:number[] = [];
    for(let i=0; i<=20; i++)
    {
        score5Counts.push(5*i);
    }



    return (
        <Flex flexDirection="column" gap={16}>
            <ButtonRow variant={"left-aligned"}>
                <H2>Graphics</H2>
            </ButtonRow>

            <DesignSystemPageSection title="ProgressBar">
                <Flex flexDirection="column" gap={4} flexGrow="1">
                    <ProgressBar completionPct={0} />
                    <ProgressBar completionPct={50} />
                    <ProgressBar completionPct={100} />
                    <ProgressBar completionPct={67} height={4} />
                    <ProgressBar completionPct={67} height={40} />
                    <ProgressBar completionPct={33} height={10} fillColor="green" />
                    <ProgressBar completionPct={50} height={10} fillColor="blue" emptyColor="lightblue" />
                </Flex>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="AggregatedSurveyAnswerScoreRadialChart">
                <Flex flexWrap="wrap">
                    {score5Counts.map((count, index) => (
                        <AggregatedSurveyAnswerScoreRadialChart key={index} stats={ScoredSelectionStats.fromDefaultCounts(count, 0, 0, 0, 100-count)} />
                    ))}
                </Flex>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="SegmentStatsComparisonGraph - indicator">
                <SegmentStatsComparisonGraph
                    type="indicator"
                    data=
                    {[
                        { segments: [{ name:'lokation', value:'Aarhus' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(1,2,4,8,16) },
                        { segments: [{ name:'lokation', value:'Hyllebjerg-Svenstrup' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(5,0,0,0,0) },
                        { segments: [{ name:'lokation', value:'København' }, { name:'afdeling', value:'Salg' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(0,0,0,0,20) },
                        { segments: [{ name:'lokation', value:'Aarhus' }, { name:'afdeling', value:'Salg' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(1,1,1,1,1) },
                        { segments: [{ name:'lokation', value:'København' }, { name:'afdeling', value:'Support' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(0,0,0,0,0) },
                        { segments: [{ name:'lokation', value:'Aarhus' }, { name:'afdeling', value:'Support' }],
                            stats: ScoredSelectionStats.fromDefaultCounts(80,20,0,0,0) },
                        { segments: [{ name:'lokation', value:'København' }, { name:'afdeling', value:'IT' }],
                            stats: ScoredSelectionStats.fromDefaultCounts(100,0,0,0,0) },
                        { segments: [
                            { name:'lokation', value:'Hyllebjerg-Svenstrup'},
                            { name:'afdeling', value:'intern product-marketing' },
                            { name:'køn', value:'mænd' },
                            { name:'ansættelsesforhold', value:'seniorer på flexjob' },
                            { name:'anciennitet', value:'20 års anciennitet' }],
                            stats: ScoredSelectionStats.fromDefaultCounts(10,0,0,0,0) }
                    ]}
                />
            </DesignSystemPageSection>

            <DesignSystemPageSection title="SegmentStatsComparisonGraph - question (default)">
                <SegmentStatsComparisonGraph
                    type="question"
                    question={{type:'default', invertScore:false, canComment:true, title:'How satisfied are you with your work?', indicators:[], translations:[]}}
                    data=
                     {[
                        { segments: [{ name:'lokation', value:'Aarhus' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(1,2,4,8,16) },
                        { segments: [{ name:'lokation', value:'Hyllebjerg-Svenstrup' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(5,0,0,0,0) },
                        { segments: [{ name:'lokation', value:'København' }, { name:'afdeling', value:'Salg' }], 
                            stats: ScoredSelectionStats.fromDefaultCounts(0,0,0,0,0) },
                     ]}
                />
            </DesignSystemPageSection>

            <DesignSystemPageSection title="SegmentStatsComparisonGraph - question (custom - 1 3 5 scale)">
                <SegmentStatsComparisonGraph
                    type="question"
                    question={{type:'custom', scored:true, canComment:true, title:'How satisfied are you with your work?', indicators:[], translations:[], 
                        options:[
                            {value:'v1', score:1, text:'Not very good', translations:[]},
                            {value:'v2', score:3, text:'Right in the middle', translations:[]},
                            {value:'v3', score:5, text:'Super duper', translations:[]}

                        ]}}
                    data=
                     {[
                        { segments: [{ name:'lokation', value:'Aarhus' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromCustomCounts([
                                {value:'v1', count:1, score:1}, 
                                {value:'v2', count:2, score:3}, 
                                {value:'v3', count:4, score:5}
                            ]) 
                        },
                        { segments: [{ name:'lokation', value:'Hyllebjerg-Svenstrup' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromCustomCounts([
                                {value:'v1', count:0, score:1}, 
                                {value:'v2', count:0, score:3}, 
                                {value:'v3', count:1, score:5}
                            ]) 
                        },
                        { segments: [{ name:'lokation', value:'Hyllebjerg-Svenstrup' }, { name:'afdeling', value:'reception' }], 
                            stats: ScoredSelectionStats.fromCustomCounts([
                                {value:'v1', count:0, score:1}, 
                                {value:'v2', count:0, score:3}, 
                                {value:'v3', count:0, score:5}
                            ]) 
                        },
                     ]}
                />
            </DesignSystemPageSection>

            <DesignSystemPageSection title="DetailedRiskAssessmentEvaluator">
                <div style={{width: "800px"}}>
                    <DetailedRiskAssessmentEvaluator
                        consequence={consequence} 
                        likelihood={likelihood} 
                        onChange={(consequence,likelihood)=>{
                            setConsequence(consequence); 
                            setLikelihood(likelihood);
                        }} />
                </div>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="RiskAssessmentEvaluator">
                <div style={{width: "600px"}}>
                    <RiskAssessmentEvaluator 
                        consequence={consequence} 
                        likelihood={likelihood} 
                        onChange={(consequence,likelihood)=>{
                            setConsequence(consequence); 
                            setLikelihood(likelihood);
                        }} />
                </div>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="DialGauge">
                <DesignSystemVariantSection title="2 / 4 segments, 200px">
                    <DialGauge
                        segments={4}
                        selectedSegments={2}
                        color="green"
                        widthPx={50}
                    />
                </DesignSystemVariantSection>
                <DesignSystemVariantSection title="1 / 3 segments, 100px">
                    <DialGauge
                        segments={3}
                        selectedSegments={1}
                        color="yellow"
                        widthPx={100}
                    />
                </DesignSystemVariantSection>
                <DesignSystemVariantSection title="5 / 10 segments, 200px">
                    <DialGauge
                        segments={10}
                        selectedSegments={5}
                        color="red"
                        widthPx={200}
                    />
                </DesignSystemVariantSection>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="Badge">
                
                <DesignSystemVariantSection title="success + TT">
                    <Badge variant="success" count={5} tooltip="This is a success badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="pending + TT">
                    <Badge variant="pending" count={2} tooltip="This is a pending badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="warning + TT">
                    <Badge variant="warning" count={1} tooltip="This is a warning badge" />
                </DesignSystemVariantSection>
                    
                <DesignSystemVariantSection title="custom icon + TT">
                    <Badge icon="actions_unavailable" count={3} tooltip="This is a custom icon badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="custom color + TT">
                    <Badge color="purple" count={3} tooltip="This is a custom color badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="custom icon + color + TT">
                    <Badge icon="actions_unavailable" color="purple" count={3} tooltip="This is a custom icon and color badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="no count + TT">
                    <Badge variant="warning" tooltip="This is a warning badge without count" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="no TT">
                    <Badge variant="success" count={10} />
                </DesignSystemVariantSection>
            </DesignSystemPageSection>


            <BorderRow>
                <H3>SegmentBar</H3>
                <Flex flexDirection="column" gap={10}>
                    <SegmentBar
                        totalSegments={4}
                        segments={[
                            { color: '#2A2' },
                            { color: '#2A2' },
                            { color: '#2A2' },
                        ]}
                    />
                    <SegmentBar
                        height={30}
                        emptyColor='black'
                        totalSegments={4}
                        segments={[
                            { color: '#CCC' },
                            { color: '#CCC' },
                            { color: '#CCC' },
                        ]}
                    />
                    <SegmentBar
                        totalSegments={4}
                        segments={[
                            { color: '#2A2' },
                            { color: '#2A2' },
                            { color: '#2A2' },
                            { color: '#2A2' }
                        ]}
                    />
                    <SegmentBar
                        totalSegments={6}
                        segments={[
                            { color: '#2A2' },
                            { color: '#2A2' }
                        ]}
                    />
                    <SegmentBar
                        totalSegments={6}
                        selectedIndex={1}
                        height={18}
                        selectionPxIncrease={6}
                        segments={[
                            { color: '#2A2', onClick: () => alert('clicked 1!') },
                            { color: '#2A2', onClick: () => alert('clicked 2!') }
                        ]}
                    />
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>VerticalBarGraph</H3>
                <div style={{ paddingBottom: '5px' }}>
                    <VerticalBarGraph barHeightPx={100} heightMode="max-value" data={[
                        { value: 0.5, color: colors.graphs.survey.score.score1 },
                        { value: 1, color: colors.graphs.survey.score.score2 },
                        { value: 4, color: colors.graphs.survey.score.score3 },
                        { value: 20, color: colors.graphs.survey.score.score4 },
                        { value: 75, color: colors.graphs.survey.score.score5, legendText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>
                <div style={{ paddingBottom: '5px' }}>
                    <VerticalBarGraph barHeightPx={100} heightMode="max-value" data={[
                        { value: 5, color: colors.graphs.survey.score.score1 },
                        { value: 10, color: colors.graphs.survey.score.score2 },
                        { value: 15, color: colors.graphs.survey.score.score3 },
                        { value: 40, color: colors.graphs.survey.score.score4 },
                        { value: 30, color: colors.graphs.survey.score.score5, legendText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>
                <div style={{ paddingBottom: '5px' }}>
                    <VerticalBarGraph barHeightPx={100} heightMode="total-sum" data={[
                        { value: 5, color: colors.graphs.survey.score.score1 },
                        { value: 10, color: colors.graphs.survey.score.score2 },
                        { value: 15, color: colors.graphs.survey.score.score3 },
                        { value: 40, color: colors.graphs.survey.score.score4 },
                        { value: 30, color: colors.graphs.survey.score.score5, legendText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>
            </BorderRow>

            <BorderRow>
                <H3>SegmentRatioBar</H3>

                <H2>Default legend, showValueText, showValueDecimals</H2>
                <SegmentRatioBar legend showValueText showValueDecimals data={[
                    { value: 0.5, color: colors.graphs.survey.score.score1 },
                    { value: 1, color: colors.graphs.survey.score.score2 },
                    { value: 4, color: colors.graphs.survey.score.score3 },
                    { value: 20, color: colors.graphs.survey.score.score4 },
                    { value: 75, color: colors.graphs.survey.score.score5 }
                ]} />

                <H2>Legend with tooltips and styling</H2>
                <div style={{ paddingBottom: '5px' }}>
                    <SegmentRatioBar legend data={[
                        { value: 0.5, color: colors.graphs.survey.score.score1 },
                        { value: 1, color: colors.graphs.survey.score.score2 },
                        { value: 4, color: colors.graphs.survey.score.score3 },
                        { value: 20, color: colors.graphs.survey.score.score4, tooltip: <span>hello</span> },
                        { value: 75, color: colors.graphs.survey.score.score5, valueText: <span style={{ color: 'white' }}>XX%</span>, tooltip: <span>hello</span> }
                    ]} />
                </div>

                <H2>No-legend</H2>
                <div style={{ paddingBottom: '5px' }}>
                    <SegmentRatioBar data={[
                        { value: 0.5, color: colors.graphs.survey.score.score1 },
                        { value: 1, color: colors.graphs.survey.score.score2 },
                        { value: 4, color: colors.graphs.survey.score.score3 },
                        { value: 20, color: colors.graphs.survey.score.score4 },
                        { value: 75, color: colors.graphs.survey.score.score5 }
                    ]} />
                </div>
            </BorderRow>

            <BorderRow>
                <H3>AnswerGraphics</H3>
                <AnswerGraphics data={{
                    stats: [
                        { date: new Date(2023, 0, 0, 1), totalStartedAnswers: 0, totalFinishedAnswers: 0 },
                        { date: new Date(2023, 0, 1, 1), totalStartedAnswers: 5, totalFinishedAnswers: 0 },
                        { date: new Date(2023, 0, 2, 1), totalStartedAnswers: 10, totalFinishedAnswers: 5 },
                        { date: new Date(2023, 0, 3, 1), totalStartedAnswers: 20, totalFinishedAnswers: 20 }
                    ]
                }} graphWidthPx={400} graphHeightPx={300} />
            </BorderRow>

            <BorderRow>
                <H3>ActivityYearRow</H3>

                {/* Before graph */}
                {/* missing */}



                <TimelineGraphYearRow graphWidthPx={480} period={{ startMonthYear:graphStartWithOffset.date, months:12 }}
                    activities={[getEvent(2, 1), getEvent(3, 2)]} />

                {/* Start before- end less than one month in- graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-1).date, end: graphStart.addDays(10).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start before- more than one month in- graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-1).date, end: graphStart.addDays(40).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start before- more than two months in- graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-1).date, end: graphStart.addDays(70).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start in- end less than one month in- graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addDays(10).date, end: graphStart.addDays(15).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start in- end more than one month in- graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addDays(10).date, end: graphStart.addDays(50).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start in- end more than two months in- graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addDays(15).date, end: graphStart.addDays(85).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start more than two months from end of- end after graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(9).addDays(10).date, end: graphStart.addYears(2).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start less than two months from end of- end after graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(10).addDays(10).date, end: graphStart.addYears(2).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

                {/* Start less than one months from end of- end after graph */}
                {/* <TimelineGraphYearRow graphWidthPx={480} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(11).addDays(10).date, end: graphStart.addYears(2).date, title: 'An A activity', description: 'Lorem ipsum...' }]} /> */}

            </BorderRow>

            <BorderRow>
                <H3>ActivityTimeline</H3>
                <ActivityGraph rowHeightPx={50} yearMonthStart={graphStart.date}
                    activities={activities} />
            </BorderRow>


            <BorderRow>
                <H3>Radials</H3>
                <Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialPieChart value="" segments={[
                            { value:1, color: colors.radialChart.red },
                            { value:1, color: colors.radialChart.green },
                            { value:1, color: colors.radialChart.yellow },
                            { value:1, color: colors.radialChart.unfilled }
                        ]} />
                        <span>RadialPieChart</span>
                    </Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialChart color="red" fillPct={67} value="67%" />
                        <span>RadialChart</span>
                    </Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialAvgChart avg={3.7} />
                        <span>RadialAvgChart</span>
                    </Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialPctChart pct={85} />
                        <span>RadialPctChart</span>
                    </Flex>
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>CompletionPanel</H3>

                30
                50
                70
                90

                <Flex gap={32}>
                    <CompletionPanel
                        pct={24}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={48}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={64}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={80}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={96}
                        completionText="En anden completionText"
                        missingText="...og en missingText"
                    />
                </Flex>

            </BorderRow>

        </Flex>
    );
};

function getEvent(dayOffset:number, dayLength:number):TimelineGraphEvent
{
    return {
        id: `${dayOffset}-${dayLength}`,
        label: `Event ${dayOffset}`,
        linkUrl: `#`,
        start: graphStart.addDays(dayOffset).date,
        end: graphStart.addDays(dayOffset + dayLength).date,
        color: "blue",
        toolTip: <>
                    <b>{`Event ${dayOffset}`}</b><br />
                    <small>{toDateString(graphStart.addDays(dayOffset).date)} - {toDateString(graphStart.addDays(dayOffset + dayLength).date)}</small>
                </>,
    } satisfies TimelineGraphEvent;
}
